import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { X, GitCompareArrows, Check, Undo2 } from "lucide-react";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { changesIn, resolveChange } from "../../lib/track";
import { flashField } from "./SigningPanel";

export const ChangesPanel = ({ rootRef, tracking, onToggle, onClose, onChanged }) => {
  const [, force] = useState(0);
  useEffect(() => {
    const mo = new MutationObserver(() => force((n) => n + 1));
    mo.observe(rootRef.current, { subtree: true, childList: true, characterData: true });
    return () => mo.disconnect();
  }, [rootRef]);
  const list = changesIn(rootRef.current);
  const act = (els, accept) => { els.forEach((el) => resolveChange(el, accept)); rootRef.current.normalize(); onChanged(); force((n) => n + 1); };

  return (
    <aside className="w-[320px] shrink-0 border-l bg-card flex flex-col" data-testid="changes-panel">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <div className="flex items-center gap-2 font-semibold text-sm"><GitCompareArrows className="h-4 w-4 text-primary" />Tracked changes <span className="text-xs font-normal text-muted-foreground" data-testid="changes-count">{list.length}</span></div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose} data-testid="changes-close-btn"><X className="h-4 w-4" /></Button>
      </div>
      <div className="p-4 space-y-3 border-b">
        <label className="flex items-center justify-between text-sm"><span>Track my changes</span><Switch checked={tracking} onCheckedChange={onToggle} data-testid="changes-track-switch" /></label>
        <p className="text-xs text-muted-foreground">While on, typed text is <span className="tc-sample-ins">underlined in colour</span> and deletions are <span className="tc-sample-del">struck through</span> instead of removed. Exports to Word keep them as real tracked changes; print and PDF show the accepted result.</p>
        <div className="flex gap-1.5">
          <Button size="sm" className="flex-1" disabled={!list.length} onClick={() => act(list, true)} data-testid="changes-accept-all"><Check className="h-4 w-4 mr-1" />Accept all</Button>
          <Button size="sm" variant="outline" className="flex-1" disabled={!list.length} onClick={() => act(list, false)} data-testid="changes-reject-all"><Undo2 className="h-4 w-4 mr-1" />Reject all</Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2" data-testid="changes-list">
        {!list.length && <p className="text-sm text-muted-foreground p-4 text-center">No tracked changes. Turn on tracking and edit the page.</p>}
        {list.map((el, i) => {
          const ins = el.classList.contains("tc-ins");
          return (
            <div key={i} className="rounded-lg px-3 py-2.5 hover:bg-accent cursor-pointer space-y-1" onClick={() => flashField(el)} data-testid={`change-item-${i}`}>
              <div className="flex items-center gap-2 text-xs">
                <span className={`font-semibold uppercase tracking-wider text-[10px] ${ins ? "text-emerald-700" : "text-red-600"}`}>{ins ? "Inserted" : "Deleted"}</span>
                <span className="font-medium" style={{ color: el.style.getPropertyValue("--tc") }}>{el.dataset.author}</span>
                <span className="text-muted-foreground">{formatDistanceToNow(Number(el.dataset.time) || Date.now(), { addSuffix: true })}</span>
              </div>
              <div className={`text-sm truncate ${ins ? "" : "line-through text-red-600"}`}>“{el.textContent}”</div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={(e) => { e.stopPropagation(); act([el], true); }} data-testid={`change-accept-${i}`}><Check className="h-3 w-3 mr-1" />Accept</Button>
                <Button size="sm" variant="ghost" className="h-6 text-xs" onClick={(e) => { e.stopPropagation(); act([el], false); }} data-testid={`change-reject-${i}`}><Undo2 className="h-3 w-3 mr-1" />Reject</Button>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};
